import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import { connect } from "react-redux"

import { setWarranty } from "areas/cart/store/actions"
import Button from "components/ui/button"
import Image from "components/ui/image"
import { isTestMode } from "helpers/application"
import { to_currency } from "helpers/number"
import { useNavigation } from "helpers/use-navigation"
import { cn } from "styles/utils"

const planItems = [
  {
    alt: "Accident Protection",
    baseOrder: 1,
    img: "fragile.svg",
    tabletOrder: 1,
    title: "Accident Protection",
    width: "37px",
  },
  {
    alt: "Easy Online Claims",
    baseOrder: 3,
    img: "shield.svg",
    tabletOrder: 2,
    title: "Easy Online Claims",
    width: "45px",
  },
  {
    alt: "Fast Replacements and Repairs",
    baseOrder: 2,
    img: "user-check.svg",
    tabletOrder: 3,
    title: "Fast Replacements + Repairs",
    width: "68px",
  },
]

const WarrantyPlanItem = ({ handleClick, plan, selected, tabIndex }) => {
  const keyboardProps = useNavigation({ direction: "horizontal" })

  return (
    <div key={`warranty-plan-${plan.value}`}>
      {plan.label && (
        <div
          className={cn(
            "text-12 rounded-t-md pb-1 pt-3 text-center text-white",
            selected ? "bg-selected" : "bg-[#4756A2]"
          )}
        >
          {plan.label}
        </div>
      )}
      <Button
        aria-checked={selected}
        aria-label={`${plan.duration} protection plan - ${to_currency(
          plan.price,
          {
            precision: 2,
          }
        )}`}
        className={cn(
          "w-88 focus:border-primary tablet:border-2 tablet:w-96 border-grey-10 h-104 focus:outline-hidden flex-col items-center justify-evenly border p-2 text-center opacity-80 transition-all duration-200 focus:border-2",
          selected &&
            "after:content-(--checkmark-icon) after:scale-80 border-selected after:size-22 relative border-2 bg-white opacity-100 after:absolute after:-bottom-12 after:left-1/2 after:block after:-translate-x-1/2",
          plan.label ? "rounded-b-md" : "rounded-md"
        )}
        data-cy={`protection-plan-${plan.duration}`}
        onClick={handleClick}
        role="checkbox"
        tabIndex={tabIndex}
        text
        {...keyboardProps}
      >
        <Image
          alt={`${plan.duration} Extend warranty`}
          height="22px"
          src={require("images/icons/extend_icon.svg")}
          width="22px"
        />
        <p className="text-12 font-bold">{plan.duration}</p>
        <p className="text-12">Protection Plan</p>
        <p className="text-12" data-cy="warranty-addon-plan-price">
          {to_currency(plan.price, { precision: 2 })}
        </p>
      </Button>
    </div>
  )
}

WarrantyPlanItem.propTypes = {
  handleClick: PropTypes.func,
  plan: PropTypes.object,
  selected: PropTypes.bool,
  tabIndex: PropTypes.number,
}

const InternalWarrantPlans = ({ item, itemWarranty, setWarranty }) => {
  return (
    <div className="tablet:flex-row gap-18 tablet:gap-26 flex flex-col items-center">
      <FocusScope>
        <div className="gap-18 tablet:gap-24 tablet:pb-16 tablet:w-auto flex w-full items-end justify-center">
          {item.warranty_plans.map((plan, index) => {
            const selected = plan.value === itemWarranty?.addon_code

            return (
              <WarrantyPlanItem
                handleClick={() => {
                  if (selected) {
                    setWarranty({})
                  } else {
                    if (!isTestMode) {
                      dataLayer.push({
                        event: "added-warranty-to-cart",
                        warrantyPlan: plan.id,
                      })
                    }
                    setWarranty(plan)
                  }
                }}
                key={`warranty-plan-${plan.value}`}
                plan={plan}
                selected={selected}
                tabIndex={selected || (!itemWarranty && index === 0) ? 0 : -1}
              />
            )
          })}
        </div>
      </FocusScope>
      <div>
        <div className="tablet:flex-col tablet:gap-0 tablet:justify-start flex flex-row flex-nowrap justify-center gap-32">
          {planItems.map((item, i) => (
            <div
              className="tablet:flex-row w-(--base-width) tablet:w-(--tablet-width) order-(--base-order) tablet:gap-8 flex flex-col items-center gap-6 text-center"
              key={`plan-item-${i}`}
              style={{
                "--base-order": item.baseOrder,
                "--base-width": item.width,
                "--tablet-order": item.tabletOrder,
                "--tablet-width": "auto",
              }}
            >
              <Image
                alt={item.alt}
                aria-hidden
                height="17px"
                src={require(`images/cart/warranty/${item.img}`)}
                width="17px"
              />
              <p className="text-10 tablet:text-14 leading-12 tablet:leading-22 font-medium tracking-tight">
                {item.title}
              </p>
            </div>
          ))}
        </div>
        <div className="tablet:mt-0 mt-10">
          <p className="tablet:block text-12 leading-22 inline font-light tracking-tight">
            Offered and sold by Extend.{" "}
          </p>
          <Button
            asChild
            className="text-12 font-light"
            text
            underline
            variant="secondary"
          >
            <a
              href="https://customers.extend.com/en-US/plan_details/A0-AAJEW-2y"
              rel="noreferrer"
              target="_blank"
            >
              Learn more about the plans
            </a>
          </Button>
        </div>
      </div>
    </div>
  )
}
const mapStateToProps = (state, ownProps) => {
  const itemWarranty = ownProps.item.addons.find(
    (a) => a.addon_type === "WARRANTY"
  )
  return {
    itemWarranty,
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const itemId = ownProps.item.id

  return {
    setWarranty: (plan) => {
      dispatch(setWarranty(itemId, plan))
    },
  }
}

InternalWarrantPlans.propTypes = {
  item: PropTypes.object,
  itemWarranty: PropTypes.object,
  setWarranty: PropTypes.func,
}

const WarrantPlans = connect(
  mapStateToProps,
  mapDispatchToProps
)(InternalWarrantPlans)

export default WarrantPlans
